/**
 * Credential View Page
 * Secure one-time credential viewing page with 2FA verification
 *
 * Users access this page via a secure link sent by email.
 * They must enter a 6-digit verification code sent to their email.
 * Credentials can only be viewed ONCE after verification.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Divider,
  useClipboard,
  useToast,
  PinInput,
  PinInputField,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import {
  MdKey,
  MdLock,
  MdContentCopy,
  MdVisibility,
  MdVisibilityOff,
  MdCheckCircle,
  MdWarning,
  MdError,
} from 'react-icons/md';
import financialServicesApi from '../../services/financialServicesApi';

// Logo component
const RelyLogo = () => (
  <Box mb={6}>
    <Heading
      size="lg"
      bgGradient="linear(to-r, blue.500, purple.600)"
      bgClip="text"
      fontWeight="bold"
    >
      Rely
    </Heading>
  </Box>
);

// Credential display component
const CredentialDisplay = ({
  label,
  value,
  isPassword = false,
  showCopy = true,
}) => {
  const [showValue, setShowValue] = useState(!isPassword);
  const { hasCopied, onCopy } = useClipboard(value || '');
  const toast = useToast();

  const handleCopy = () => {
    onCopy();
    toast({
      title: 'Copiado',
      description: `${label} copiado al portapapeles`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  if (!value) return null;

  return (
    <Box w="full">
      <Text fontSize="sm" color="gray.500" mb={1}>
        {label}
      </Text>
      <HStack
        bg="gray.50"
        border="1px"
        borderColor="gray.200"
        borderRadius="md"
        p={3}
        justify="space-between"
      >
        <Text
          fontFamily="mono"
          fontSize="md"
          color="gray.800"
          flex={1}
          wordBreak="break-all"
        >
          {isPassword && !showValue ? '••••••••••••' : value}
        </Text>
        <HStack spacing={1}>
          {isPassword && (
            <IconButton
              icon={<Icon as={showValue ? MdVisibilityOff : MdVisibility} />}
              size="sm"
              variant="ghost"
              aria-label={showValue ? 'Ocultar' : 'Mostrar'}
              onClick={() => setShowValue(!showValue)}
            />
          )}
          {showCopy && (
            <IconButton
              icon={<Icon as={MdContentCopy} />}
              size="sm"
              variant="ghost"
              aria-label="Copiar"
              onClick={handleCopy}
              colorScheme={hasCopied ? 'green' : 'gray'}
            />
          )}
        </HStack>
      </HStack>
    </Box>
  );
};

// Main component
const CredentialViewPage = () => {
  const [searchParams] = useSearchParams();
  const toast = useToast();

  // Get token from URL
  const accessToken = searchParams.get('token');

  // State management
  const [step, setStep] = useState('verify'); // verify, view, acknowledged, error, expired
  const [verificationCode, setVerificationCode] = useState('');
  const [credentials, setCredentials] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes timeout for viewing

  const pinInputRef = useRef(null);

  // Validate token on mount
  useEffect(() => {
    if (!accessToken) {
      setStep('error');
      setError('Link inválido. No se encontró el token de acceso.');
    }
  }, [accessToken]);

  // Countdown timer when viewing credentials
  useEffect(() => {
    let timer;
    if (step === 'view' && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setStep('expired');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, countdown]);

  // Format countdown time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle verification code submission
  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setError('Por favor ingresa el código de 6 dígitos');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await financialServicesApi.verifyCredentialCode(
        accessToken,
        verificationCode
      );

      if (result.success) {
        // After verification, immediately fetch credentials
        const viewResult = await financialServicesApi.viewCredentials(
          accessToken
        );

        if (viewResult.success) {
          setCredentials(viewResult);
          setStep('view');
          toast({
            title: 'Verificación exitosa',
            description: 'Ahora puedes ver tus credenciales',
            status: 'success',
            duration: 3000,
          });
        } else {
          setError(viewResult.error || 'Error al obtener credenciales');
          if (
            viewResult.error?.includes('already viewed') ||
            viewResult.error?.includes('ya vistas')
          ) {
            setStep('expired');
            setError(
              'Estas credenciales ya fueron vistas anteriormente. Por razones de seguridad, solo pueden ser vistas una vez.'
            );
          }
        }
      } else {
        setError(result.error || 'Código inválido');
        setRemainingAttempts(result.remainingAttempts);
        setIsLocked(result.locked);

        if (result.locked) {
          setStep('error');
          setError(
            'Demasiados intentos fallidos. El acceso ha sido bloqueado por seguridad.'
          );
        }

        // Clear the pin input
        setVerificationCode('');
      }
    } catch (err) {
      setError('Error de conexión. Por favor intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle acknowledge
  const handleAcknowledge = async () => {
    setIsLoading(true);

    try {
      const result = await financialServicesApi.acknowledgeCredentials(
        accessToken
      );

      if (result.success) {
        setStep('acknowledged');
        toast({
          title: 'Confirmado',
          description: 'Has confirmado que guardaste tus credenciales',
          status: 'success',
          duration: 3000,
        });
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Error al confirmar',
          status: 'error',
          duration: 3000,
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Error de conexión',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Error state - invalid or missing token
  if (!accessToken || step === 'error') {
    return (
      <Box
        minH="100vh"
        bg="gray.50"
        py={12}
        px={4}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="md">
          <Card>
            <CardBody>
              <VStack spacing={6} textAlign="center">
                <RelyLogo />
                <Icon as={MdError} w={16} h={16} color="red.500" />
                <Heading size="md" color="gray.700">
                  {isLocked ? 'Acceso Bloqueado' : 'Link Inválido'}
                </Heading>
                <Text color="gray.600">
                  {error || 'Este link no es válido o ha expirado.'}
                </Text>
                <Button
                  colorScheme="blue"
                  onClick={() =>
                    (window.location.href = 'https://rely.business')
                  }
                >
                  Ir a Rely
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  // Expired state - credentials already viewed or timed out
  if (step === 'expired') {
    return (
      <Box
        minH="100vh"
        bg="gray.50"
        py={12}
        px={4}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="md">
          <Card>
            <CardBody>
              <VStack spacing={6} textAlign="center">
                <RelyLogo />
                <Icon as={MdWarning} w={16} h={16} color="orange.500" />
                <Heading size="md" color="gray.700">
                  Credenciales No Disponibles
                </Heading>
                <Text color="gray.600">
                  {error || 'El tiempo para ver las credenciales ha expirado.'}
                </Text>
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertDescription>
                      Si necesitas acceso nuevamente, contacta a tu asesor de
                      Rely para que te reenvíe las credenciales.
                    </AlertDescription>
                  </Box>
                </Alert>
                <Button
                  colorScheme="blue"
                  onClick={() =>
                    (window.location.href = 'https://app.rely.business')
                  }
                >
                  Ir al Dashboard
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  // Acknowledged state - user confirmed they saved credentials
  if (step === 'acknowledged') {
    return (
      <Box
        minH="100vh"
        bg="gray.50"
        py={12}
        px={4}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="md">
          <Card>
            <CardBody>
              <VStack spacing={6} textAlign="center">
                <RelyLogo />
                <Icon as={MdCheckCircle} w={16} h={16} color="green.500" />
                <Heading size="md" color="gray.700">
                  ¡Credenciales Guardadas!
                </Heading>
                <Text color="gray.600">
                  Has confirmado que guardaste tus credenciales de forma segura.
                  Ya puedes cerrar esta página.
                </Text>
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Próximos pasos</AlertTitle>
                    <AlertDescription>
                      Usa estas credenciales para acceder a tu cuenta en{' '}
                      {credentials?.providerName || 'el proveedor'}.
                    </AlertDescription>
                  </Box>
                </Alert>
                <Button
                  colorScheme="blue"
                  onClick={() =>
                    (window.location.href = 'https://app.rely.business')
                  }
                >
                  Ir al Dashboard de Rely
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  // Verification step
  if (step === 'verify') {
    return (
      <Box
        minH="100vh"
        bg="gray.50"
        py={12}
        px={4}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="md">
          <Card>
            <CardHeader pb={2}>
              <VStack spacing={4} textAlign="center">
                <RelyLogo />
                <Icon as={MdLock} w={12} h={12} color="blue.500" />
                <Heading size="md" color="gray.700">
                  Verificación de Seguridad
                </Heading>
                <Text color="gray.600" fontSize="sm">
                  Ingresa el código de 6 dígitos que enviamos a tu email para
                  ver tus credenciales.
                </Text>
              </VStack>
            </CardHeader>

            <CardBody>
              <VStack spacing={6}>
                {error && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertDescription>{error}</AlertDescription>
                      {remainingAttempts !== null && remainingAttempts > 0 && (
                        <Text fontSize="sm" mt={1}>
                          Intentos restantes: {remainingAttempts}
                        </Text>
                      )}
                    </Box>
                  </Alert>
                )}

                <VStack spacing={4} w="full">
                  <Text fontSize="sm" color="gray.500">
                    Código de verificación
                  </Text>
                  <Flex justify="center">
                    <HStack>
                      <PinInput
                        ref={pinInputRef}
                        value={verificationCode}
                        onChange={setVerificationCode}
                        otp
                        size="lg"
                        isDisabled={isLoading}
                        onComplete={handleVerify}
                      >
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                      </PinInput>
                    </HStack>
                  </Flex>
                </VStack>

                <Button
                  colorScheme="blue"
                  size="lg"
                  w="full"
                  onClick={handleVerify}
                  isLoading={isLoading}
                  isDisabled={verificationCode.length !== 6}
                >
                  Verificar
                </Button>

                <Alert status="info" borderRadius="md" variant="subtle">
                  <AlertIcon />
                  <Box fontSize="sm">
                    <AlertDescription>
                      El código fue enviado al email asociado con tu cuenta.
                      Revisa tu bandeja de spam si no lo encuentras.
                    </AlertDescription>
                  </Box>
                </Alert>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  // View credentials step
  if (step === 'view') {
    return (
      <Box
        minH="100vh"
        bg="gray.50"
        py={12}
        px={4}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="md">
          <Card>
            <CardHeader pb={2}>
              <VStack spacing={4} textAlign="center">
                <RelyLogo />
                <Icon as={MdKey} w={12} h={12} color="green.500" />
                <Heading size="md" color="gray.700">
                  Tus Credenciales de {credentials?.providerName || 'Cuenta'}
                </Heading>
              </VStack>
            </CardHeader>

            <CardBody>
              <VStack spacing={6}>
                {/* Countdown warning */}
                <Alert
                  status={countdown < 60 ? 'warning' : 'info'}
                  borderRadius="md"
                >
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">
                      Tiempo restante: {formatTime(countdown)}
                    </AlertTitle>
                    <AlertDescription fontSize="xs">
                      Guarda estas credenciales ahora. No podrás acceder a ellas
                      nuevamente.
                    </AlertDescription>
                  </Box>
                </Alert>

                {/* Credentials display */}
                <VStack spacing={4} w="full" divider={<Divider />}>
                  <CredentialDisplay
                    label="Usuario / Email"
                    value={credentials?.username}
                    isPassword={false}
                  />
                  <CredentialDisplay
                    label="Contraseña"
                    value={credentials?.password}
                    isPassword={true}
                  />
                  {credentials?.notes && (
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500" mb={1}>
                        Notas Adicionales
                      </Text>
                      <Box
                        bg="yellow.50"
                        border="1px"
                        borderColor="yellow.200"
                        borderRadius="md"
                        p={3}
                      >
                        <Text
                          fontSize="sm"
                          color="gray.700"
                          whiteSpace="pre-wrap"
                        >
                          {credentials.notes}
                        </Text>
                      </Box>
                    </Box>
                  )}
                </VStack>

                <Divider />

                {/* Security notice */}
                <Alert status="warning" borderRadius="md" variant="subtle">
                  <AlertIcon />
                  <Box fontSize="sm">
                    <AlertTitle>Importante</AlertTitle>
                    <AlertDescription>
                      Guarda estas credenciales en un lugar seguro (gestor de
                      contraseñas recomendado). No las compartas con nadie.
                    </AlertDescription>
                  </Box>
                </Alert>

                {/* Acknowledge button */}
                <Button
                  colorScheme="green"
                  size="lg"
                  w="full"
                  onClick={handleAcknowledge}
                  isLoading={isLoading}
                  leftIcon={<Icon as={MdCheckCircle} />}
                >
                  Confirmo que guardé mis credenciales
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  // Default loading state
  return (
    <Box
      minH="100vh"
      bg="gray.50"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Spinner size="xl" color="blue.500" />
    </Box>
  );
};

export default CredentialViewPage;
